import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, postForFile, qs } from "@/lib/api";
import { useKeys } from "@/lib/queries";
import type {
  VoiceCatalog, VoiceSettings, VoiceSettingsInput, VoiceTestInput,
} from "@/lib/voice-settings";

/**
 * core-api's `/integrations/voice` (ADR-114 in platform). Admins only, as the routes are. A
 * superadmin with more than one tenant names the tenant; everybody else gets their own.
 */

/** A tenant's voice settings, and who speaks in each case. */
export function useVoiceSettings(tenantId?: number, enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["voice-settings", tenantId ?? "own"]),
    queryFn: () => api.get<VoiceSettings>(`/integrations/voice${qs({ tenant_id: tenantId })}`),
    enabled,
  });
}

/**
 * The voices the tenant's key can use and the models that speak a text, read from ElevenLabs each
 * time. Asked only once a key is saved, and not tried again by itself: a key ElevenLabs no longer
 * takes does not start taking it, and the screen has a button.
 */
export function useVoiceCatalog(tenantId?: number, enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["voice-catalog", tenantId ?? "own"]),
    queryFn: () => api.get<VoiceCatalog>(`/integrations/voice/catalog${qs({ tenant_id: tenantId })}`),
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}

/**
 * Save the settings. core-api asks ElevenLabs about the key and every voice first, so this can take
 * a second or two. The catalog is read again afterwards: another key is another account's voices.
 */
export function useSaveVoiceSettings(tenantId?: number) {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: VoiceSettingsInput) =>
      api.put<VoiceSettings>("/integrations/voice", tenantId ? { ...input, tenant_id: tenantId } : input),
    onSuccess: (saved) => {
      qc.setQueryData(keys.scoped(["voice-settings", tenantId ?? "own"]), saved);
      void qc.invalidateQueries({ queryKey: keys.scoped(["voice-catalog", tenantId ?? "own"]) });
    },
  });
}

/** Disconnect from ElevenLabs: the key and every voice picked are removed here. */
export function useDisconnectVoices(tenantId?: number) {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<void>(`/integrations/voice${qs({ tenant_id: tenantId })}`),
    onSuccess: () => {
      qc.removeQueries({ queryKey: keys.scoped(["voice-catalog", tenantId ?? "own"]) });
      return qc.invalidateQueries({ queryKey: keys.scoped(["voice-settings", tenantId ?? "own"]) });
    },
  });
}

/** A sentence spoken with the settings as they are saved. The answer is the sound. */
export function useVoiceTest(tenantId?: number) {
  return useMutation({
    mutationFn: async (input: VoiceTestInput) =>
      (await postForFile("/integrations/voice/test", tenantId ? { ...input, tenant_id: tenantId } : input)).blob,
  });
}
